/**
 * WHF Theme — Block interactivity.
 *
 * Handles JavaScript behaviour for ACF custom blocks.
 */

import { initGallery } from './gallery.js';
import { mountQuiz } from "./blocks/quiz.js";

export function initBlocks() {
	initVideoPlayers();
	initVideoContent();
	initSliders();
	initGallery();
	initArchiveFilters();
	initProgrammeTabs();
	initProgrammePrint();
	initHorizontalTimelines();
	initScrollableTimelines();
	initMicrositeNav();
	initTimelineLineHeights();
	initShareButtons();
	initPodcastToggles();
	initMaps();
	initAudienceSwitchers();
	initPeopleBioModal();
	initQuiz();
}

// ---------------------------------------------------------------------------
// People Grid — "Read bio" modal (when the block's Bio display = Modal).
//
// Each grid renders one [data-people-modal]; clicking a person's
// [data-bio-toggle] populates it from that person's hidden bio data and opens
// it. Esc / backdrop / close button dismiss; focus is trapped while open and
// returned to the trigger on close.
// ---------------------------------------------------------------------------
function initPeopleBioModal() {
	if ( ! document.querySelector( '[data-people-modal]' ) ) return;

	const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
	let current = null;
	let lastFocus = null;

	const close = () => {
		if ( ! current ) return;
		current.setAttribute( 'hidden', '' );
		current.classList.remove( 'is-open' );
		document.body.style.overflow = '';
		if ( lastFocus && lastFocus.focus ) lastFocus.focus();
		current = null;
	};

	document.addEventListener( 'click', ( e ) => {
		const toggle = e.target.closest( '[data-bio-toggle]' );
		if ( toggle ) {
			const grid = toggle.closest( '.whf-people-grid' );
			const modal = grid && grid.querySelector( '[data-people-modal]' );
			const data = toggle.parentElement.querySelector( '.whf-people-grid__bio-data' );
			if ( ! modal || ! data ) return;

			const name = data.getAttribute( 'data-bio-name' ) || '';
			const meta = data.getAttribute( 'data-bio-meta' ) || '';
			modal.querySelector( '[data-modal-name]' ).textContent = name;
			const metaEl = modal.querySelector( '[data-modal-meta]' );
			metaEl.textContent = meta;
			metaEl.hidden = ! meta;
			modal.querySelector( '[data-modal-bio]' ).innerHTML = data.innerHTML;

			const dialog = modal.querySelector( '.whf-people-grid__modal-dialog' );
			if ( dialog && name ) dialog.setAttribute( 'aria-label', name );

			lastFocus = toggle;
			modal.removeAttribute( 'hidden' );
			void modal.offsetWidth; // reflow so the open transition runs
			modal.classList.add( 'is-open' );
			document.body.style.overflow = 'hidden';
			current = modal;

			const closeBtn = modal.querySelector( '.whf-people-grid__modal-close' );
			if ( closeBtn ) closeBtn.focus();
			return;
		}

		if ( current && e.target.closest( '[data-people-modal-close]' ) ) {
			close();
		}
	} );

	document.addEventListener( 'keydown', ( e ) => {
		if ( ! current ) return;
		if ( 'Escape' === e.key ) {
			close();
			return;
		}
		if ( 'Tab' === e.key ) {
			const dialog = current.querySelector( '.whf-people-grid__modal-dialog' );
			const focusable = Array.prototype.slice.call( dialog.querySelectorAll( FOCUSABLE ) ).filter( ( el ) => el.offsetParent !== null );
			if ( ! focusable.length ) return;
			const first = focusable[ 0 ];
			const last = focusable[ focusable.length - 1 ];
			if ( e.shiftKey && ( document.activeElement === first || document.activeElement === dialog ) ) {
				e.preventDefault();
				last.focus();
			} else if ( ! e.shiftKey && document.activeElement === last ) {
				e.preventDefault();
				first.focus();
			}
		}
	} );
}

// ---------------------------------------------------------------------------
// Audience Switcher block — tab bar swaps which panel is active.
//
// Sequence on tab click:
//   1. Tabs flip immediately so the user gets feedback on what they tapped.
//   2. Loading progress bar (root.is-loading) fills 0→100% over ~400ms —
//      content is already in the DOM, but the bar gives the perception
//      that the change "took effect".
//   3. Active panel cross-fades to the new one.
//   4. Smooth-scroll the panels container into view so the new content
//      starts at the top of the viewport.
// ---------------------------------------------------------------------------
function initAudienceSwitchers() {
	const switchers = document.querySelectorAll( '[data-audience-switcher]' );
	const PROGRESS_MS = 400;

	// Remember the visitor's audience across pages/visits. Keyed by panel
	// slug (stable if tabs are reordered) and shared, so the choice carries
	// between switchers wherever the same audience exists.
	const STORAGE_KEY = 'whf-audience';
	const readAudience = () => {
		try {
			return window.localStorage.getItem( STORAGE_KEY );
		} catch ( e ) {
			return null;
		}
	};
	const saveAudience = ( slug ) => {
		try {
			window.localStorage.setItem( STORAGE_KEY, slug );
		} catch ( e ) {
			// Storage unavailable (private mode etc.) — non-fatal.
		}
	};

	switchers.forEach( ( root ) => {
		const panelsContainer = root.querySelector( '[data-audience-panels]' );
		if ( ! panelsContainer ) {
			return;
		}

		const panels = Array.from(
			panelsContainer.querySelectorAll( '[data-audience-panel]' )
		);
		if ( ! panels.length ) {
			return;
		}

		// Build the selector from the panels — keeps it in sync with the
		// actual rendered tabs regardless of ACF's innerBlocks quirks.
		// Editors choose a button group (default) or a dropdown, which is
		// better when there are a lot of audiences.
		const selectorStyle = root.getAttribute( 'data-audience-selector' ) || 'buttons';
		const tabBar = document.createElement( 'div' );
		tabBar.className = 'whf-audience-switcher__tabs';

		const prompt = root.getAttribute( 'data-audience-prompt' );
		if ( prompt ) {
			const promptEl = document.createElement( 'span' );
			promptEl.className = 'whf-audience-switcher__prompt';
			promptEl.textContent = prompt;
			tabBar.appendChild( promptEl );
		}

		const labelFor = ( panel, index ) =>
			panel.getAttribute( 'data-audience-label' ) || 'Audience ' + ( index + 1 );

		// Syncs whichever control (buttons / dropdown) to the active index.
		let setControlActive;

		if ( 'dropdown' === selectorStyle ) {
			const select = document.createElement( 'select' );
			select.className = 'whf-audience-switcher__select';
			if ( prompt ) {
				select.setAttribute( 'aria-label', prompt );
			}
			panels.forEach( ( panel, index ) => {
				const opt = document.createElement( 'option' );
				opt.value = String( index );
				opt.textContent = labelFor( panel, index );
				opt.selected = 0 === index;
				select.appendChild( opt );
			} );
			select.addEventListener( 'change', () => activate( parseInt( select.value, 10 ) ) );

			// Wrap so the custom chevron (::after) can inherit currentColor
			// and adapt to the block background.
			const selectWrap = document.createElement( 'div' );
			selectWrap.className = 'whf-audience-switcher__select-wrap';
			selectWrap.appendChild( select );
			tabBar.appendChild( selectWrap );

			setControlActive = ( index ) => {
				select.value = String( index );
			};
		} else {
			tabBar.setAttribute( 'role', 'tablist' );
			if ( prompt ) {
				tabBar.setAttribute( 'aria-label', prompt );
			}
			// Buttons live in their own centred row below the prompt.
			const tabList = document.createElement( 'div' );
			tabList.className = 'whf-audience-switcher__tab-list';
			tabBar.appendChild( tabList );

			const tabs = panels.map( ( panel, index ) => {
				const btn = document.createElement( 'button' );
				btn.type = 'button';
				btn.className = 'whf-audience-switcher__tab' + ( 0 === index ? ' is-active' : '' );
				btn.setAttribute( 'role', 'tab' );
				btn.setAttribute( 'aria-selected', 0 === index ? 'true' : 'false' );
				btn.textContent = labelFor( panel, index );
				btn.addEventListener( 'click', () => activate( index ) );
				tabList.appendChild( btn );
				return btn;
			} );

			setControlActive = ( index ) => {
				tabs.forEach( ( tab, i ) => {
					const on = i === index;
					tab.classList.toggle( 'is-active', on );
					tab.setAttribute( 'aria-selected', on ? 'true' : 'false' );
				} );
			};
		}

		// Insert the tab bar just before the panels container.
		panelsContainer.parentNode.insertBefore( tabBar, panelsContainer );

		let busy = false;
		let currentIndex = 0;

		const setActive = ( index ) => {
			setControlActive( index );
			panels.forEach( ( panel, i ) => {
				panel.classList.toggle( 'is-active', i === index );
			} );
		};

		// Restore the saved audience if it matches one of this switcher's
		// panels; otherwise default to the first. Set directly (no loading
		// animation or scroll) so the page simply opens on the right panel.
		const savedSlug = readAudience();
		const savedIndex = savedSlug
			? panels.findIndex( ( p ) => p.getAttribute( 'data-audience-panel' ) === savedSlug )
			: -1;
		currentIndex = savedIndex >= 0 ? savedIndex : 0;
		setActive( currentIndex );
		root.classList.add( 'is-ready' );

		// Function declaration so the control handlers above can call it.
		function activate( index ) {
			if ( busy || index === currentIndex ) {
				return;
			}
			busy = true;

			// Remember the choice for next time / other pages.
			saveAudience( panels[ index ].getAttribute( 'data-audience-panel' ) );

			// Step 1: control flips immediately.
			setControlActive( index );

			// Step 2: progress bar fills.
			root.classList.add( 'is-loading' );

			window.setTimeout( () => {
				// Step 3: panel cross-fade.
				panels.forEach( ( panel, i ) => {
					panel.classList.toggle( 'is-active', i === index );
				} );

				// Step 4: scroll the new content into view.
				panelsContainer.scrollIntoView( { behavior: 'smooth', block: 'start' } );

				window.setTimeout( () => {
					root.classList.remove( 'is-loading' );
					currentIndex = index;
					busy = false;
				}, 200 );
			}, PROGRESS_MS );
		}
	} );
}

// ---------------------------------------------------------------------------
// Map block — lazy-loads D3 only on pages that actually contain a map.
// ---------------------------------------------------------------------------
function initMaps() {
	const maps = document.querySelectorAll( '[data-map-instance]' );
	if ( ! maps.length ) return;

	import( /* webpackChunkName: "whf-map" */ './blocks/map.js' )
		.then( ( { mountMap } ) => {
			maps.forEach( ( root ) => mountMap( root ) );
		} )
		.catch( ( err ) => {
			// Fallback list remains visible; no further action needed.
			// eslint-disable-next-line no-console
			console.warn( 'whf-map: failed to load renderer', err );
		} );
}

// ---------------------------------------------------------------------------
// Video Player — click thumbnail to load embed
// ---------------------------------------------------------------------------
function initVideoPlayers() {
	const triggers = document.querySelectorAll('[data-video-trigger]');
	if (!triggers.length) return;

	triggers.forEach((btn) => {
		btn.addEventListener('click', () => {
			const itemId = btn.getAttribute('data-video-trigger');
			const item = document.getElementById(itemId);
			if (!item) return;

			// Embed container — the video block uses .whf-video__embed; other
			// blocks (e.g. text-media) opt in with a [data-video-embed] element.
			const embedContainer = item.querySelector('.whf-video__embed, [data-video-embed]');
			if (!embedContainer) return;

			const videoFile = item.getAttribute('data-video-file');
			if (videoFile) {
				// Self-hosted MP4 / WebM — play inline with native controls.
				embedContainer.innerHTML = `<video src="${videoFile}" controls autoplay playsinline></video>`;
			} else {
				const videoUrl = item.getAttribute('data-video-url');
				if (!videoUrl) return;
				const embedUrl = getEmbedUrl(videoUrl);
				if (!embedUrl) return;
				embedContainer.innerHTML = `<iframe src="${embedUrl}" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>`;
			}

			embedContainer.hidden = false;
			item.classList.add('whf-video__item--playing');
			item.classList.add('is-video-playing');
		});
	});
}

// ---------------------------------------------------------------------------
// Video content panel — clamp long descriptions to a few lines with a
// Read more / Read less toggle. Slide navigation collapses any expanded panel
// (see collapseVideoContent, called from the slider).
// ---------------------------------------------------------------------------
function initVideoContent() {
	document.querySelectorAll('.whf-video__content-wrap').forEach((wrap) => {
		const content = wrap.querySelector('.whf-video__content');
		const btn = wrap.querySelector('.whf-video__readmore');
		if (!content || !btn) return;

		// Only show the toggle when the content actually overflows the clamp.
		// (clientHeight reflects the CSS max-height; scrollHeight is the full text.)
		const overflows = () => content.scrollHeight > content.clientHeight + 4;
		if (!overflows()) return;
		btn.hidden = false;

		btn.addEventListener('click', () => {
			const expanded = content.classList.toggle('is-expanded');
			if (expanded) {
				content.style.maxHeight = content.scrollHeight + 'px';
				btn.setAttribute('aria-expanded', 'true');
				btn.textContent = btn.dataset.lessLabel || 'Read less';
			} else {
				content.style.maxHeight = '';
				btn.setAttribute('aria-expanded', 'false');
				btn.textContent = btn.dataset.moreLabel || 'Read more';
			}
		});
	});
}

/**
 * Stop any playing video within a scope (e.g. a slider grid) by tearing down its
 * embed — this halts playback/audio and restores the thumbnail so it can be
 * replayed. Called on slide change so the video you were watching stops when you
 * move to another slide.
 */
function resetVideoPlayers(scope) {
	scope.querySelectorAll('.whf-video__item--playing').forEach((item) => {
		const embed = item.querySelector('.whf-video__embed, [data-video-embed]');
		if (embed) {
			embed.innerHTML = '';
			embed.hidden = true;
		}
		item.classList.remove('whf-video__item--playing', 'is-video-playing');
	});
}

/**
 * Collapse any expanded video content panels within a scope (e.g. a slider grid).
 * Called on slide change so moving to another video minimises the open panel.
 */
function collapseVideoContent(scope) {
	scope.querySelectorAll('.whf-video__content.is-expanded').forEach((content) => {
		content.classList.remove('is-expanded');
		content.style.maxHeight = '';
		const btn = content.parentElement.querySelector('.whf-video__readmore');
		if (btn) {
			btn.setAttribute('aria-expanded', 'false');
			btn.textContent = btn.dataset.moreLabel || 'Read more';
		}
	});
}

/**
 * Convert a YouTube/Vimeo URL into an embeddable iframe src.
 */
function getEmbedUrl(url) {
	// YouTube
	let match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
	if (match) {
		return `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0`;
	}

	// Vimeo
	match = url.match(/vimeo\.com\/(\d+)/);
	if (match) {
		return `https://player.vimeo.com/video/${match[1]}?autoplay=1`;
	}

	return null;
}

// ---------------------------------------------------------------------------
// Archive Filters — mobile sidebar toggle + collapsible facet sections
// ---------------------------------------------------------------------------
function initArchiveFilters() {
	const toggle = document.querySelector( '.whf-archive-layout__filter-toggle' );
	const sidebar = document.querySelector( '.whf-archive-layout__sidebar' );
	if ( !toggle || !sidebar ) return;

	// Dimmed backdrop behind the popup card (created lazily, reused after).
	let backdrop = null;
	const ensureBackdrop = () => {
		if ( backdrop ) return backdrop;
		backdrop = document.createElement( 'div' );
		backdrop.className = 'whf-archive-layout__sidebar-backdrop';
		backdrop.addEventListener( 'click', closeSidebar );
		document.body.appendChild( backdrop );
		return backdrop;
	};

	const openSidebar = () => {
		sidebar.classList.add( 'is-open' );
		toggle.setAttribute( 'aria-expanded', 'true' );
		document.body.style.overflow = 'hidden';
		const bd = ensureBackdrop();
		// Force reflow so the opacity transition runs.
		void bd.offsetWidth;
		bd.classList.add( 'is-visible' );
		// Move focus into the drawer (the toggle itself hides while open). Focus
		// the region rather than the search field so the mobile keyboard doesn't
		// pop up unbidden; Tab from here lands on the first control.
		sidebar.setAttribute( 'tabindex', '-1' );
		sidebar.focus();
	};

	const closeSidebar = () => {
		sidebar.classList.remove( 'is-open' );
		toggle.setAttribute( 'aria-expanded', 'false' );
		document.body.style.overflow = '';
		if ( backdrop ) {
			backdrop.classList.remove( 'is-visible' );
		}
		// Return focus to the toggle that opened the drawer.
		toggle.focus();
	};

	// Close on Escape.
	document.addEventListener( 'keydown', ( e ) => {
		if ( 'Escape' === e.key && sidebar.classList.contains( 'is-open' ) ) {
			closeSidebar();
		}
	} );

	// Trap Tab focus within the open drawer so it can't reach the page behind it.
	sidebar.addEventListener( 'keydown', ( e ) => {
		if ( 'Tab' !== e.key || ! sidebar.classList.contains( 'is-open' ) ) return;
		const focusable = Array.prototype.slice.call( sidebar.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
		) ).filter( ( el ) => el.offsetParent !== null );
		if ( ! focusable.length ) return;
		const first = focusable[ 0 ];
		const last = focusable[ focusable.length - 1 ];
		if ( e.shiftKey && ( document.activeElement === first || document.activeElement === sidebar ) ) {
			e.preventDefault();
			last.focus();
		} else if ( ! e.shiftKey && document.activeElement === last ) {
			e.preventDefault();
			first.focus();
		}
	} );

	toggle.addEventListener( 'click', () => {
		if ( sidebar.classList.contains( 'is-open' ) ) {
			closeSidebar();
		} else {
			openSidebar();
		}
	} );

	// "Show results" button closes the sidebar.
	const showResultsBtn = sidebar.querySelector( '.whf-archive-layout__show-results-btn' );
	if ( showResultsBtn ) {
		showResultsBtn.addEventListener( 'click', closeSidebar );
	}

	// Collapsible facet headings.
	const headings = sidebar.querySelectorAll( '.whf-archive-layout__facet-heading[aria-expanded]' );
	headings.forEach( ( btn ) => {
		btn.addEventListener( 'click', () => {
			const expanded = btn.getAttribute( 'aria-expanded' ) === 'true';
			const bodyId = btn.getAttribute( 'aria-controls' );
			const body = document.getElementById( bodyId );
			if ( !body ) return;

			btn.setAttribute( 'aria-expanded', !expanded );
			body.hidden = expanded;
		} );
	} );

	// On mobile the sidebar is a drawer (the filter toggle is visible), so start
	// with every facet section collapsed to save space — the visitor expands the
	// ones they want. Desktop keeps them open. NB the toggle is position:fixed,
	// so offsetParent is always null even when visible — test display instead.
	if ( 'none' !== window.getComputedStyle( toggle ).display ) {
		headings.forEach( ( btn ) => {
			const body = document.getElementById( btn.getAttribute( 'aria-controls' ) );
			if ( body ) {
				btn.setAttribute( 'aria-expanded', 'false' );
				body.hidden = true;
			}
		} );
	}
}

// ---------------------------------------------------------------------------
// Slider — responsive paginated grid with dot navigation + swipe
// ---------------------------------------------------------------------------
function initSliders() {
	const grids = document.querySelectorAll( '[data-slider="true"]' );
	if ( !grids.length ) return;

	const BP_SM = 576;
	const BP_MD = 768;
	const BP_LG = 1024;

	grids.forEach( ( grid ) => {
		const desktopPerPage = parseInt( grid.getAttribute( 'data-per-page' ), 10 ) || 3;
		const pagination = grid.parentElement.querySelector( '[class*="__pagination"]' );
		if ( !pagination ) return;

		// Optional thumbnail strip (gallery slider) — server-rendered buttons
		// with [data-page]. Wired to paging + kept in sync alongside the dots.
		const thumbs = grid.parentElement.querySelector( '[data-slider-thumbs]' );
		// Opt-outs / opt-ins (gallery): no arrows; height animates to the
		// active slide's content (so short slides don't leave a big gap).
		const showArrows = grid.getAttribute( 'data-arrows' ) !== 'false';
		const adaptiveHeight = grid.hasAttribute( 'data-adaptive-height' );

		// Toggle the active state on every paging control (dots + thumbs).
		const syncActive = ( page ) => {
			[ pagination, thumbs ].forEach( ( wrap ) => {
				if ( !wrap ) return;
				wrap.querySelectorAll( 'button[data-page]' ).forEach( ( b ) => {
					b.classList.toggle( 'is-active', parseInt( b.getAttribute( 'data-page' ), 10 ) === page );
				} );
			} );
		};

		if ( thumbs ) {
			thumbs.querySelectorAll( 'button[data-page]' ).forEach( ( btn ) => {
				btn.addEventListener( 'click', () => {
					scrollToPage( parseInt( btn.getAttribute( 'data-page' ), 10 ) );
					stopAuto();
				} );
			} );
		}

		const totalCards = grid.children.length;
		let currentPage = 0;
		let effectivePerPage = desktopPerPage;

		// Auto-slide state.
		let autoInterval = null;
		let autoStopped = false;
		const hasAutoSlide = grid.hasAttribute( 'data-auto-slide' );

		/**
		 * Calculate effective per-page based on viewport width.
		 */
		const getEffectivePerPage = () => {
			const w = window.innerWidth;
			if ( w < BP_SM ) return 1;
			if ( w < BP_MD ) return Math.min( 2, desktopPerPage );
			if ( w < BP_LG ) return Math.min( 2, desktopPerPage );
			return desktopPerPage;
		};

		/**
		 * Build dot buttons inside the pagination container.
		 */
		const buildDots = ( pageCount ) => {
			pagination.innerHTML = '';
			for ( let i = 0; i < pageCount; i++ ) {
				const btn = document.createElement( 'button' );
				btn.className = pagination.className.replace( /.*__pagination.*/, '' ).trim();
				// Use the first child class prefix for dot class (e.g. whf-card-grid__dot).
				const paginationClass = [ ...pagination.classList ].find( ( c ) => c.includes( '__pagination' ) );
				const dotClass = paginationClass ? paginationClass.replace( '__pagination', '__dot' ) : '';
				btn.className = dotClass + ( i === 0 ? ' is-active' : '' );
				btn.setAttribute( 'data-page', i );
				btn.setAttribute( 'aria-label', 'Page ' + ( i + 1 ) );
				btn.addEventListener( 'click', () => {
					scrollToPage( i );
					stopAuto();
				} );
				pagination.appendChild( btn );
			}
		};

		// SVG chevron shared by both arrows (prev is flipped in CSS).
		const arrowSvg = '<svg width="8" height="12" viewBox="0 0 8 12" fill="none" aria-hidden="true" focusable="false"><path d="M1.2364 0L0 1.213L4.559 5.713L0 10.213L1.2364 11.427L7.058 5.713L1.2364 0Z" fill="currentColor"/></svg>';

		/**
		 * Insert fresh prev/next arrow buttons into the dot row. Called after
		 * buildDots() (which clears the pagination container), so prev is
		 * prepended and next appended, flanking the dots.
		 */
		const buildArrows = () => {
			const makeBtn = ( dir ) => {
				const btn = document.createElement( 'button' );
				btn.type = 'button';
				btn.className = 'whf-slider-' + dir;
				btn.setAttribute( 'aria-label', 'prev' === dir ? 'Previous slide' : 'Next slide' );
				btn.innerHTML = arrowSvg;
				btn.addEventListener( 'click', () => {
					const totalPages = Math.ceil( totalCards / effectivePerPage );
					const target = 'prev' === dir ? currentPage - 1 : currentPage + 1;
					if ( target < 0 || target > totalPages - 1 ) return;
					scrollToPage( target );
					stopAuto();
				} );
				return btn;
			};
			pagination.insertBefore( makeBtn( 'prev' ), pagination.firstChild );
			pagination.appendChild( makeBtn( 'next' ) );
		};

		/**
		 * Set the CSS variable used to vertically centre the side arrows on the
		 * cards (the arrows sit in the dot row in the DOM, so they can't use a
		 * plain top:50%). No-op until the grid has laid out.
		 */
		const updateArrowPosition = () => {
			if ( !grid.parentElement ) return;
			const top = grid.offsetTop + ( grid.offsetHeight / 2 );
			grid.parentElement.style.setProperty( '--whf-slider-arrow-top', top + 'px' );
		};

		/**
		 * Grey out (disable) the prev arrow on the first page and the next arrow
		 * on the last — rather than hiding them — so the controls never shift.
		 */
		const updateArrows = () => {
			const totalPages = Math.ceil( totalCards / effectivePerPage );
			const prevBtn = pagination.querySelector( '.whf-slider-prev' );
			const nextBtn = pagination.querySelector( '.whf-slider-next' );
			if ( prevBtn ) prevBtn.disabled = ( currentPage <= 0 );
			if ( nextBtn ) nextBtn.disabled = ( currentPage >= totalPages - 1 );
			updateArrowPosition();
		};

		/**
		 * Update CSS variable + dots for current breakpoint.
		 */
		const recalculate = () => {
			const newPerPage = getEffectivePerPage();
			const changed = newPerPage !== effectivePerPage;
			effectivePerPage = newPerPage;

			grid.style.setProperty( '--slider-per-page', effectivePerPage );

			const totalPages = Math.ceil( totalCards / effectivePerPage );
			buildDots( totalPages );

			// Prev + next arrows. They live in the dot row (inline alongside the
			// bullets); on viewports >= 1400px CSS lifts them out to the left/
			// right edges of the cards. Opt out via data-arrows="false".
			if ( showArrows && totalPages > 1 && grid.parentElement ) {
				grid.parentElement.style.position = 'relative';
				buildArrows();
			}

			// Clamp current page.
			if ( currentPage >= totalPages ) {
				currentPage = Math.max( 0, totalPages - 1 );
			}

			// Dots (and any inline arrows) were just rebuilt — resync state.
			syncActive( currentPage );
			updateArrows();

			if ( changed ) {
				scrollToPage( currentPage );
			}

			setAdaptiveHeight();
		};

		/**
		 * The scrollLeft a given page actually settles at. Mirrors the
		 * browser's own clamping: a page's start card can sit further right
		 * than the grid can scroll to (e.g. a partial last page), so this
		 * clamps to the real max — keeping scrollToPage() and
		 * syncDotsToScroll() reading from the same reachable position.
		 */
		const pageScrollLeft = ( page ) => {
			const card = grid.children[ page * effectivePerPage ];
			if ( !card ) return 0;
			const maxScroll = grid.scrollWidth - grid.clientWidth;
			return Math.min( card.offsetLeft, maxScroll );
		};

		/**
		 * Scroll to a specific page and update active dot.
		 */
		const scrollToPage = ( page ) => {
			currentPage = page;

			// Moving to another slide stops the video you were watching and
			// minimises any expanded video content panel.
			resetVideoPlayers( grid );
			collapseVideoContent( grid );

			if ( grid.children[ page * effectivePerPage ] ) {
				grid.scrollTo( { left: pageScrollLeft( page ), behavior: 'smooth' } );
			}

			syncActive( page );

			updateArrows();
			setAdaptiveHeight();
		};

		/**
		 * Animate the slider's height to the active slide's content height, so
		 * a short slide doesn't leave a gap above the controls and a tall one
		 * isn't clipped. No-op unless data-adaptive-height is set.
		 */
		const setAdaptiveHeight = () => {
			if ( !adaptiveHeight ) return;
			const active = grid.children[ currentPage * effectivePerPage ];
			if ( active ) {
				grid.style.height = active.offsetHeight + 'px';
			}
			// Height changed — re-centre the side arrows on the new card height.
			updateArrowPosition();
		};

		/**
		 * Detect current page from scroll position and update dots.
		 */
		let scrollTimer = null;
		const syncDotsToScroll = () => {
			clearTimeout( scrollTimer );
			scrollTimer = setTimeout( () => {
				const cards = grid.children;
				let closestPage = 0;
				let closestDist = Infinity;

				for ( let i = 0; i < cards.length; i += effectivePerPage ) {
					const page = Math.floor( i / effectivePerPage );
					const dist = Math.abs( pageScrollLeft( page ) - grid.scrollLeft );
					if ( dist < closestDist ) {
						closestDist = dist;
						closestPage = page;
					}
				}

				if ( closestPage !== currentPage ) {
					currentPage = closestPage;
					resetVideoPlayers( grid );
					collapseVideoContent( grid );
					syncActive( closestPage );
					updateArrows();
					setAdaptiveHeight();
				}
			}, 100 );
		};

		// ---------------------------------------------------------------
		// Auto-slide
		// ---------------------------------------------------------------
		const startAuto = () => {
			if ( autoInterval || autoStopped || !hasAutoSlide ) return;
			const totalPages = Math.ceil( totalCards / effectivePerPage );
			autoInterval = setInterval( () => {
				const next = ( currentPage + 1 ) % totalPages;
				scrollToPage( next );
			}, 5000 );
		};

		const stopAuto = () => {
			autoStopped = true;
			if ( autoInterval ) {
				clearInterval( autoInterval );
				autoInterval = null;
			}
		};

		// Temporary pause for pause-on-hover. Unlike stopAuto() it does not set
		// autoStopped, so autoplay resumes on mouseleave. Genuine interaction
		// (dot / arrow / swipe) still calls stopAuto() for a permanent stop.
		const pauseAuto = () => {
			if ( autoInterval ) {
				clearInterval( autoInterval );
				autoInterval = null;
			}
		};

		// ---------------------------------------------------------------
		// Touch swipe
		// ---------------------------------------------------------------
		let touchStartX = 0;
		let touchStartY = 0;
		let isSwiping = false;

		grid.addEventListener( 'touchstart', ( e ) => {
			touchStartX = e.touches[0].clientX;
			touchStartY = e.touches[0].clientY;
			isSwiping = false;
		}, { passive: true } );

		grid.addEventListener( 'touchmove', ( e ) => {
			if ( isSwiping ) return;
			const dx = Math.abs( e.touches[0].clientX - touchStartX );
			const dy = Math.abs( e.touches[0].clientY - touchStartY );
			// Lock to horizontal if swipe angle is more horizontal than vertical.
			if ( dx > dy && dx > 10 ) {
				isSwiping = true;
			}
		}, { passive: true } );

		grid.addEventListener( 'touchend', ( e ) => {
			const delta = touchStartX - e.changedTouches[0].clientX;
			if ( Math.abs( delta ) > 50 ) {
				stopAuto();
				const totalPages = Math.ceil( totalCards / effectivePerPage );
				if ( delta > 0 && currentPage < totalPages - 1 ) {
					scrollToPage( currentPage + 1 );
				} else if ( delta < 0 && currentPage > 0 ) {
					scrollToPage( currentPage - 1 );
				}
			}
		}, { passive: true } );

		// ---------------------------------------------------------------
		// Init + listeners
		// ---------------------------------------------------------------
		grid.addEventListener( 'scroll', syncDotsToScroll, { passive: true } );

		// Recalculate on resize (debounced).
		let resizeTimer = null;
		window.addEventListener( 'resize', () => {
			clearTimeout( resizeTimer );
			resizeTimer = setTimeout( recalculate, 150 );
		} );

		// Initial setup.
		recalculate();

		// Re-measure the adaptive height once images have loaded.
		if ( adaptiveHeight ) {
			grid.querySelectorAll( 'img' ).forEach( ( img ) => {
				if ( img.complete ) return;
				img.addEventListener( 'load', setAdaptiveHeight, { once: true } );
			} );
		}

		if ( hasAutoSlide ) {
			grid.addEventListener( 'touchstart', stopAuto, { passive: true } );
			// Pause on hover, resume on leave (unless a real interaction has
			// permanently stopped it).
			grid.addEventListener( 'mouseenter', pauseAuto );
			grid.addEventListener( 'mouseleave', startAuto );

			// Pause autoplay while the slider is scrolled out of view. Otherwise
			// it keeps cycling off-screen — and for an adaptive-height slider that
			// would keep changing the slider's height and shift the page content
			// below it while the user is reading elsewhere.
			if ( 'IntersectionObserver' in window ) {
				new IntersectionObserver( ( entries ) => {
					entries.forEach( ( entry ) => {
						if ( entry.isIntersecting ) {
							startAuto();
						} else {
							pauseAuto();
						}
					} );
				}, { threshold: 0 } ).observe( grid );
			}

			startAuto();
		}
	} );
}

// ---------------------------------------------------------------------------
// Programme day tabs
// ---------------------------------------------------------------------------
function initProgrammeTabs() {
	document.querySelectorAll( '.whf-programme__tabs' ).forEach( ( tablist ) => {
		const tabs = tablist.querySelectorAll( '.whf-programme__tab' );
		const programme = tablist.closest( '.whf-programme' );
		if ( !programme ) return;

		const panels = programme.querySelectorAll( '.whf-programme__day' );

		tabs.forEach( ( tab ) => {
			tab.addEventListener( 'click', () => {
				const dayIdx = tab.dataset.day;

				// Update tabs.
				tabs.forEach( ( t ) => {
					t.classList.remove( 'is-active' );
					t.setAttribute( 'aria-selected', 'false' );
				} );
				tab.classList.add( 'is-active' );
				tab.setAttribute( 'aria-selected', 'true' );

				// Show/hide panels.
				panels.forEach( ( panel, idx ) => {
					if ( String( idx ) === dayIdx ) {
						panel.classList.remove( 'is-hidden' );
					} else {
						panel.classList.add( 'is-hidden' );
					}
				} );
			} );
		} );
	} );
}

// ---------------------------------------------------------------------------
// Programme — "Download Programme" print/PDF button
//
// When no custom PDF is attached the button generates a printable version of
// the programme: it flags the body so the print stylesheet isolates the
// programme (and reveals every day, not just the active tab), then opens the
// browser print dialog (where the user can "Save as PDF"). State is cleaned up
// on afterprint.
// ---------------------------------------------------------------------------
function initProgrammePrint() {
	document.querySelectorAll( '[data-whf-programme-print]' ).forEach( ( btn ) => {
		btn.addEventListener( 'click', () => {
			const programme = btn.closest( '.whf-programme' );
			if ( ! programme ) return;

			programme.classList.add( 'whf-programme--printing' );
			document.body.classList.add( 'whf-print-programme' );

			const cleanup = () => {
				document.body.classList.remove( 'whf-print-programme' );
				programme.classList.remove( 'whf-programme--printing' );
				window.removeEventListener( 'afterprint', cleanup );
			};
			window.addEventListener( 'afterprint', cleanup );

			window.print();
		} );
	} );
}

// ---------------------------------------------------------------------------
// Podcast — supplementary toggle
// ---------------------------------------------------------------------------
function initPodcastToggles() {
	const toggles = document.querySelectorAll( '[data-podcast-toggle]' );
	if ( ! toggles.length ) return;

	toggles.forEach( ( btn ) => {
		const panel = btn.nextElementSibling;
		if ( ! panel || ! panel.hasAttribute( 'data-podcast-panel' ) ) return;

		btn.addEventListener( 'click', () => {
			const isOpen = btn.getAttribute( 'aria-expanded' ) === 'true';
			btn.setAttribute( 'aria-expanded', ! isOpen );
			panel.hidden = isOpen;
		} );
	} );
}

// ---------------------------------------------------------------------------
// Horizontal Timeline — tab switching with optional auto-advance
// ---------------------------------------------------------------------------
function initHorizontalTimelines() {
	const timelines = document.querySelectorAll( '[data-timeline-panels]' );
	if ( ! timelines.length ) return;

	const ADVANCE_INTERVAL = 6000; // ms per tab when auto-advancing

	timelines.forEach( ( panelsContainer ) => {
		const wrapper = panelsContainer.closest( '.whf-timeline--horizontal' );
		if ( ! wrapper ) return;

		const tabsNav = wrapper.querySelector( '[data-timeline-tabs]' );
		if ( ! tabsNav ) return;

		const panels = panelsContainer.querySelectorAll( '[data-timeline-panel]' );
		const tabs   = tabsNav.querySelectorAll( '[data-timeline-tab]' );
		const autoAdvance = panelsContainer.hasAttribute( 'data-auto-advance' );

		let currentIndex = 0;
		let advanceTimer = null;


		function goTo( index, shouldScroll ) {
			// Reset all tabs and panels.
			panels.forEach( ( p ) => p.classList.remove( 'is-active' ) );
			tabs.forEach( ( t ) => {
				t.classList.remove( 'is-active', 'is-advancing' );
				const progress = t.querySelector( '.whf-timeline__tab-progress' );
				if ( progress ) {
					progress.style.width = '0';
					progress.style.transitionDuration = '0s';
				}
			} );

			// Activate current panel + tab.
			currentIndex = index;
			if ( panels[ index ] ) {
				panels[ index ].classList.add( 'is-active' );
			}
			if ( tabs[ index ] ) {
				tabs[ index ].classList.add( 'is-active' );
			}

			// Fill progress bars for all tabs before the active one (already visited).
			tabs.forEach( ( t, i ) => {
				if ( i < index ) {
					const p = t.querySelector( '.whf-timeline__tab-progress' );
					if ( p ) {
						p.style.transitionDuration = '0s';
						p.style.width = '100%';
					}
				}
			} );

			// Scroll active tab into view (only on manual click).
			if ( shouldScroll && tabs[ index ] ) {
				tabs[ index ].scrollIntoView( { behavior: 'smooth', inline: 'center', block: 'nearest' } );
			}

			if ( autoAdvance ) {
				startAutoAdvance();
			}
		}

		function startAutoAdvance() {
			stopAutoAdvance();

			// Animate the ACTIVE tab's progress bar — shows how long until next.
			const tab = tabs[ currentIndex ];
			if ( ! tab ) return;

			const progress = tab.querySelector( '.whf-timeline__tab-progress' );
			if ( progress ) {
				progress.style.width = '0';
				progress.style.transitionDuration = '0s';
				tab.classList.remove( 'is-advancing' );

				requestAnimationFrame( () => {
					requestAnimationFrame( () => {
						tab.classList.add( 'is-advancing' );
						progress.style.transitionDuration = ADVANCE_INTERVAL + 'ms';
						progress.style.width = '100%';
					} );
				} );
			}

			advanceTimer = setTimeout( () => {
				const next = ( currentIndex + 1 ) % panels.length;
				goTo( next );
			}, ADVANCE_INTERVAL );
		}

		function stopAutoAdvance() {
			if ( advanceTimer ) {
				clearTimeout( advanceTimer );
				advanceTimer = null;
			}
		}

		// Tab click handlers — manual click stops auto-advance.
		tabs.forEach( ( tab ) => {
			tab.addEventListener( 'click', () => {
				stopAutoAdvance();
				// Reset all progress bars.
				tabs.forEach( ( t ) => {
					t.classList.remove( 'is-advancing' );
					const p = t.querySelector( '.whf-timeline__tab-progress' );
					if ( p ) {
						p.style.width = '0';
						p.style.transitionDuration = '0s';
					}
				} );
				const index = parseInt( tab.getAttribute( 'data-timeline-tab' ), 10 );

				// Switch panel without restarting auto-advance.
				panels.forEach( ( p ) => p.classList.remove( 'is-active' ) );
				tabs.forEach( ( t ) => t.classList.remove( 'is-active' ) );

				currentIndex = index;
				if ( panels[ index ] ) panels[ index ].classList.add( 'is-active' );
				if ( tabs[ index ] ) tabs[ index ].classList.add( 'is-active' );

				// Fill bars for visited tabs, active tab filled static.
				tabs.forEach( ( t, i ) => {
					if ( i <= index ) {
						const p = t.querySelector( '.whf-timeline__tab-progress' );
						if ( p ) {
							p.style.transitionDuration = '0.3s';
							p.style.width = '100%';
						}
					}
				} );

				if ( tabs[ index ] ) {
					tabs[ index ].scrollIntoView( { behavior: 'smooth', inline: 'center', block: 'nearest' } );
				}
			} );
		} );

		// Touch swipe support on panels.
		let touchStartX = 0;
		panelsContainer.addEventListener( 'touchstart', ( e ) => {
			touchStartX = e.touches[0].clientX;
			stopAutoAdvance();
		}, { passive: true } );

		panelsContainer.addEventListener( 'touchend', ( e ) => {
			const diff = touchStartX - e.changedTouches[0].clientX;
			if ( Math.abs( diff ) > 50 ) {
				if ( diff > 0 && currentIndex < panels.length - 1 ) {
					goTo( currentIndex + 1 );
				} else if ( diff < 0 && currentIndex > 0 ) {
					goTo( currentIndex - 1 );
				}
			} else if ( autoAdvance ) {
				startAutoAdvance();
			}
		}, { passive: true } );

		// Pause auto-advance on hover.
		if ( autoAdvance ) {
			wrapper.addEventListener( 'mouseenter', stopAutoAdvance );
			wrapper.addEventListener( 'mouseleave', () => startAutoAdvance() );
		}

		// Show first panel.
		goTo( 0, false );
	} );
}

// ---------------------------------------------------------------------------
// Scrollable Timeline — fade + arrow hint, hide on scroll end
// ---------------------------------------------------------------------------
function initScrollableTimelines() {
	const scrollWraps = document.querySelectorAll( '[data-timeline-scroll]' );
	if ( ! scrollWraps.length ) return;

	scrollWraps.forEach( ( wrap ) => {
		const timeline = wrap.closest( '.whf-timeline--scrollable' );
		if ( ! timeline ) return;

		// The actual scrolling element is __line inside the wrap.
		const scrollEl = wrap.querySelector( '.whf-timeline__line' );
		if ( ! scrollEl ) return;

		let ticking = false;

		const checkScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = scrollEl;
			const isScrollable = scrollHeight > clientHeight + 10;
			const atBottom = scrollTop + clientHeight >= scrollHeight - 40;

			if ( ! isScrollable || atBottom ) {
				timeline.classList.add( 'is-scrolled-end' );
			} else {
				timeline.classList.remove( 'is-scrolled-end' );
			}

			// Show top gradient once scrolled down.
			if ( scrollTop > 20 ) {
				timeline.classList.add( 'is-scrolling-down' );
			} else {
				timeline.classList.remove( 'is-scrolling-down' );
			}

			ticking = false;
		};

		scrollEl.addEventListener( 'scroll', () => {
			if ( ! ticking ) {
				requestAnimationFrame( checkScroll );
				ticking = true;
			}
		}, { passive: true } );

		// Delay initial check to ensure content has rendered.
		requestAnimationFrame( () => {
			requestAnimationFrame( checkScroll );
		} );
	} );
}

// ---------------------------------------------------------------------------
// Timeline line heights — measure items and set CSS custom property
// ---------------------------------------------------------------------------
function initTimelineLineHeights() {
	const lines = document.querySelectorAll( '.whf-timeline__line' );
	if ( ! lines.length ) return;

	const setHeights = () => {
		lines.forEach( ( line ) => {
			const items = line.querySelectorAll( '.whf-timeline__item' );
			if ( ! items.length ) return;

			const lastItem = items[ items.length - 1 ];
			const lineHeight = lastItem.offsetTop + lastItem.offsetHeight / 2;
			line.style.setProperty( '--timeline-line-height', lineHeight + 'px' );
		} );
	};

	requestAnimationFrame( () => {
		requestAnimationFrame( setHeights );
	} );

	window.addEventListener( 'resize', () => {
		requestAnimationFrame( setHeights );
	} );
}

// ---------------------------------------------------------------------------
// Microsite Nav — click chevron to expand/collapse children
// ---------------------------------------------------------------------------
function initMicrositeNav() {
	const chevrons = document.querySelectorAll( '.whf-single-event__section-chevron' );
	if ( ! chevrons.length ) return;

	chevrons.forEach( ( chevron ) => {
		chevron.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			e.stopPropagation();

			const item = chevron.closest( '.whf-single-event__section-item' );
			if ( ! item ) return;

			const children = item.querySelector( '.whf-single-event__section-children' );
			if ( ! children ) return;

			const isOpen = children.classList.contains( 'is-open' );
			children.classList.toggle( 'is-open' );
			item.classList.toggle( 'is-expanded' );
		} );
	} );
}

// ---------------------------------------------------------------------------
// Share Button — toggle dropdown, copy link, close on outside click
// ---------------------------------------------------------------------------
function initShareButtons() {
	const shares = document.querySelectorAll( '[data-share]' );
	if ( ! shares.length ) return;

	shares.forEach( ( share ) => {
		const toggle = share.querySelector( '[data-share-toggle]' );
		const menu = share.querySelector( '[data-share-menu]' );
		if ( ! toggle || ! menu ) return;

		toggle.addEventListener( 'click', ( e ) => {
			e.stopPropagation();
			const isOpen = toggle.getAttribute( 'aria-expanded' ) === 'true';
			toggle.setAttribute( 'aria-expanded', ! isOpen );
			menu.hidden = isOpen;
		} );

		// Copy link button.
		const copyBtn = share.querySelector( '[data-share-copy]' );
		if ( copyBtn ) {
			copyBtn.addEventListener( 'click', () => {
				const url = copyBtn.getAttribute( 'data-share-copy' );
				navigator.clipboard.writeText( url ).then( () => {
					const label = copyBtn.querySelector( '[data-share-copy-label]' );
					if ( label ) {
						const original = label.textContent;
						label.textContent = 'Copied!';
						copyBtn.classList.add( 'whf-share__item--copied' );
						setTimeout( () => {
							label.textContent = original;
							copyBtn.classList.remove( 'whf-share__item--copied' );
						}, 2000 );
					}
				} );
			} );
		}
	} );

	// Close all on outside click.
	document.addEventListener( 'click', () => {
		shares.forEach( ( share ) => {
			const toggle = share.querySelector( '[data-share-toggle]' );
			const menu = share.querySelector( '[data-share-menu]' );
			if ( toggle && menu ) {
				toggle.setAttribute( 'aria-expanded', 'false' );
				menu.hidden = true;
			}
		} );
	} );
}

function initQuiz() {
	import('./blocks/quiz.js').then(mountQuiz);
}

document.addEventListener('click', async (event) => {
	const button = event.target.closest('.whf-stories__load-more');
	if (!button) {
		return;
	}

	const feed = button.closest('.whf-stories');
	console.log(feed);

	const currentPage = parseInt(feed.dataset.page, 10);
	const nextPage = currentPage + 1;
	const limit = parseInt(feed.dataset.limit, 10);
	const country = feed.dataset.country;

	button.disabled = true;

	const params = new URLSearchParams({
		page: nextPage,
		limit: limit,
		country: country,
	});

	try {
		const response = await fetch(`/wp-json/whf/v1/stories?${params}`);

		if (!response.ok) {
			throw new Error('Failed to load stories.');
		}

		const data = await response.json();

		feed.querySelector('.whf-post-feed__grid')
			.insertAdjacentHTML(
			'beforeend',
				data.html
			);

		feed.dataset.page = nextPage;

		if (!data.has_more) {
			button.remove();
		} else {
			button.disabled = false;
		}
	} catch (error) {
		console.error(error);
		button.disabled = false;
	}
});
